import { Fragment, useState, useMemo, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut';
import { useKeyboardShortcutsModal } from '@/hooks/useKeyboardShortcutsModal';

import FaIcon from './FaIcon';
import SearchInput from './SearchInput';
// Platform detection
const isMac = typeof navigator !== 'undefined' &&
  (navigator.platform.includes('Mac') || navigator.userAgent.includes('Mac'));

// Icons








interface KeyboardShortcut {
  keys: string[];
  description: string;
  category: string;
}

// Define all keyboard shortcuts
const KEYBOARD_SHORTCUTS: KeyboardShortcut[] = [
  // Global shortcuts
  { keys: ['Ctrl', 'K'], description: 'Open command palette', category: 'Global' },
  { keys: ['?'], description: 'Show keyboard shortcuts', category: 'Global' },
  { keys: ['Esc'], description: 'Close modals and panels', category: 'Global' },
  { keys: ['Ctrl', '/'], description: 'Toggle AI Assistant', category: 'Global' },

  // Navigation shortcuts
  { keys: ['G', 'D'], description: 'Go to Dashboard', category: 'Navigation' },
  { keys: ['G', 'M'], description: 'Go to Map', category: 'Navigation' },
  { keys: ['G', 'I'], description: 'Go to Incidents', category: 'Navigation' },
  { keys: ['G', 'T'], description: 'Go to Tasks', category: 'Navigation' },
  { keys: ['G', 'F'], description: 'Go to Forms', category: 'Navigation' },
  { keys: ['G', 'S'], description: 'Go to Shelters', category: 'Navigation' },
  { keys: ['G', 'U'], description: 'Go to Team', category: 'Navigation' },
  { keys: ['G', 'A'], description: 'Go to Audit Logs', category: 'Navigation' },
  { keys: ['G', 'C'], description: 'Go to Settings', category: 'Navigation' },

  // Actions
  { keys: ['R'], description: 'Refresh current view', category: 'Actions' },
  { keys: ['/'], description: 'Focus search field', category: 'Actions' },

  // Command Palette
  { keys: ['↑', '↓'], description: 'Navigate commands', category: 'Command Palette' },
  { keys: ['Enter'], description: 'Execute command', category: 'Command Palette' },
  { keys: ['Esc'], description: 'Close palette', category: 'Command Palette' },

  // Map
  { keys: ['+', '='], description: 'Zoom in', category: 'Map' },
  { keys: ['-', '_'], description: 'Zoom out', category: 'Map' },
  { keys: ['Esc'], description: 'Clear selection', category: 'Map' },

  // Messaging
  { keys: ['Ctrl', 'M'], description: 'Toggle messaging panel', category: 'Messaging' },
  { keys: ['Enter'], description: 'Send message', category: 'Messaging' },
  { keys: ['Shift', 'Enter'], description: 'New line in message', category: 'Messaging' },
];

/** Map generic key labels to platform-specific labels on Mac */
function platformKey(key: string): string {
  if (!isMac) return key;
  switch (key) {
    case 'Ctrl': return '⌘';
    case 'Alt': return '⌥';
    case 'Shift': return '⇧';
    default: return key;
  }
}

// Key component for displaying individual keys
function Key({ children }: { children: string }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[2rem] h-7 px-2 text-xs font-semibold text-theme-primary bg-surface-primary border border-theme-secondary rounded shadow-sm">
      {platformKey(children)}
    </kbd>
  );
}

// Shortcut row component
function ShortcutRow({ shortcut }: { shortcut: KeyboardShortcut }) {
  return (
    <div className="shortcut-row flex items-center justify-between py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 px-3 -mx-3 rounded-lg transition-colors">
      <span className="text-sm text-theme-secondary">{shortcut.description}</span>
      <div className="flex items-center gap-1.5">
        {shortcut.keys.map((key, index) => (
          <Fragment key={index}>
            {index > 0 && <span className="text-theme-tertiary text-xs">then</span>}
            <Key>{key}</Key>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export default function KeyboardShortcutsModal() {
  const { isOpen, close } = useKeyboardShortcutsModal();
  const [searchQuery, setSearchQuery] = useState('');

  // Register ? keyboard shortcut to open modal
  useKeyboardShortcut({
    key: '?',
    onTrigger: () => useKeyboardShortcutsModal.getState().toggle(),
    ignoreInputFields: true,
  });

  // Filter shortcuts based on search query
  const filteredShortcuts = useMemo(() => {
    if (!searchQuery.trim()) return KEYBOARD_SHORTCUTS;

    const query = searchQuery.toLowerCase();
    return KEYBOARD_SHORTCUTS.filter(
      (shortcut) =>
        shortcut.description.toLowerCase().includes(query) ||
        shortcut.category.toLowerCase().includes(query) ||
        shortcut.keys.some((key) => key.toLowerCase().includes(query))
    );
  }, [searchQuery]);

  // Group shortcuts by category
  const groupedShortcuts = useMemo(() => {
    const groups: Record<string, KeyboardShortcut[]> = {};

    filteredShortcuts.forEach((shortcut) => {
      if (!groups[shortcut.category]) {
        groups[shortcut.category] = [];
      }
      groups[shortcut.category].push(shortcut);
    });

    return groups;
  }, [filteredShortcuts]);

  /*
   * Mark the body only while this sheet is open.
   *
   * The print stylesheet hides every direct child of <body> except this
   * dialog, so the shortcuts print on their own page. Applied unconditionally
   * that rule hid `#root` on every print in the app — Ctrl+P anywhere, and the
   * shift-handoff and at-risk-registrant print buttons, all produced a blank
   * sheet. Scoping it to this class keeps the isolation for the one dialog
   * that wants it.
   */
  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add('printing-shortcuts');
    return () => document.body.classList.remove('printing-shortcuts');
  }, [isOpen]);

  const handleClose = () => {
    close();
    setSearchQuery('');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999] keyboard-shortcuts-print" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[10000] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel className="relative w-full max-w-4xl transform overflow-hidden rounded-xl bg-surface-elevated shadow-2xl transition-all">
                {/* Header */}
                <div className="border-b border-gray-200 dark:border-gray-800 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FaIcon icon="circle-question" className="h-5 w-5" />
                      <div>
                        <Dialog.Title className="text-xl font-semibold text-theme-primary">
                          Keyboard Shortcuts
                        </Dialog.Title>
                        <p className="text-sm text-theme-tertiary mt-0.5">
                          Press <Key>?</Key> anytime to view this guide
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
                      aria-label="Close"
                    >
                      <FaIcon icon="xmark" className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Search */}
                  <div className="mt-4">
                    <SearchInput
                      placeholder="Search shortcuts..."
                      aria-label="Search shortcuts"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoFocus
                      className="text-sm"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="max-h-[60vh] overflow-y-auto">
                  {Object.keys(groupedShortcuts).length > 0 ? (
                    <div className="px-6 py-4 space-y-6">
                      {Object.entries(groupedShortcuts).map(([category, shortcuts]) => (
                        <div key={category}>
                          <h3 className="text-sm font-semibold text-theme-primary uppercase tracking-wider mb-3">
                            {category}
                          </h3>
                          <div className="space-y-1">
                            {shortcuts.map((shortcut, index) => (
                              <ShortcutRow key={`${category}-${index}`} shortcut={shortcut} />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-6 py-12 text-center">
                      <p className="text-sm text-theme-tertiary">
                        No shortcuts found matching "{searchQuery}"
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4 print-hide">
                      <span className="text-theme-secondary">
                        Platform: <span className="font-medium text-theme-primary">{isMac ? 'macOS' : 'Windows/Linux'}</span>
                      </span>
                      <a
                        href="/help/getting-started/navigation"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                      >
                        <FaIcon icon="file-lines" className="h-4 w-4" />
                        <span>Full documentation</span>
                      </a>
                    </div>
                    <div className="flex items-center gap-2 print-hide">
                      <button
                        type="button"
                        onClick={handlePrint}
                        className="px-4 py-2 text-theme-secondary hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      >
                        Print
                      </button>
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
